import React, {useEffect} from 'react';
import {ProSidebar, SidebarContent, SidebarFooter, SidebarHeader} from 'react-pro-sidebar';
import {useDispatch, useSelector} from 'react-redux';
import 'react-pro-sidebar/dist/css/styles.css';
import {loggedInUserSelector} from '../../../store/selectors/auth.selectors';
import SidebarItem, {SidebarMenuItem} from './SidebarItem';
import {Role} from '../../../incase-domain/user';
import NobulaLogoIcon from '../../../../assets/icons/NobulaLogoWhite.svg';
import ArrowLeftIcon from '../../../../assets/icons/ArrowLeft.svg?react';
import ArrowRightIcon from '../../../../assets/icons/ArrowRight.svg?react';
import NobulaLogoTextIcon from '../../../../assets/icons/NobulaLogoText.svg?react';
import {toggleCollapseAction} from '../../../store/actions/sidebar';

interface SidebarProps {
    logo?: string;
    backgroundColor?: string;
    secondaryColor?: string;
    collapsed: boolean;
}

Sidebar.defaultProps = {
    logo: NobulaLogoIcon,
    backgroundColor: '#2D678B',
    secondaryColor: '#211e4e',
};

function Sidebar(props: SidebarProps): JSX.Element {
    const {logo, backgroundColor, secondaryColor, collapsed} = props;

    const dispatch = useDispatch();

    const loggedInUser = useSelector(loggedInUserSelector);

    const sidebarMenuItems: SidebarMenuItem[] = [
        {
            icon: 'solar:home-2-bold',
            iconUnselected: 'solar:home-2-linear',
            text: 'home',
            url: '/',
        },
        {
            icon: 'solar:presentation-graph-bold',
            iconUnselected: 'solar:presentation-graph-linear',
            text: 'editor',
            url: '/presentations',
        },
        {
            icon: 'solar:play-circle-bold',
            iconUnselected: 'solar:play-circle-linear',
            text: 'rooms',
            url: '/rooms',
        },
        {
            icon: 'solar:users-group-rounded-bold',
            iconUnselected: 'solar:users-group-rounded-linear',
            text: 'connections',
            url: '/connections',
        },
        {
            icon: 'solar:shield-user-bold',
            iconUnselected: 'solar:shield-user-linear',
            text: 'users',
            url: '/users',
            roles: [Role.SUPERUSER, Role.ADMIN],
        },
        {
            icon: 'solar:magic-stick-bold',
            iconUnselected: 'solar:magic-stick-linear',
            text: 'generator',
            url: '/generator',
            roles: [Role.SUPERUSER],
        },
        {
            icon: 'solar:shield-keyhole-bold',
            iconUnselected: 'solar:shield-keyhole-linear',
            text: 'dashboard',
            url: '/dashboard',
            roles: [Role.SUPERUSER, Role.ADMIN, Role.CREATOR],
            footer: true,
        },
        {
            icon: 'solar:settings-bold',
            iconUnselected: 'solar:settings-outline',
            text: 'settings',
            url: '/settings',
            footer: true,
        },
    ];

    useEffect(() => {
        document.documentElement.style.setProperty('--icon-color', `${backgroundColor}`);
    }, [backgroundColor]);

    useEffect(() => {
        document.documentElement.style.setProperty('--icon-color-hover', `${secondaryColor}`);
    }, [secondaryColor]);

    const filteredMenuItemsForRole = sidebarMenuItems.filter((item) => {
        if (item.roles) return item.roles?.some((role) => loggedInUser?.roles?.includes(role));

        return true;
    });

    return (
        <>
            <div id="c-sidebar" className="c-sidebar" style={{backgroundColor: `${backgroundColor}`}}>
                <ProSidebar collapsed={collapsed}>
                    <SidebarHeader>
                        <div className="c-sidebar__menuHeader">
                            <div className="c-sidebar__logoAndTitleContainer">
                                {collapsed ? (
                                    <img src={`${logo}`} alt="" className="c-sidebar__logoIconSize" />
                                ) : (
                                    <div className="c-sidebar__logoAndTitle">
                                        <div className="c-sidebar__logoIcon c-sidebar__logoIconSize">
                                            <img src={`${logo}`} alt="" className="c-sidebar__svgImage" />
                                        </div>
                                        <div className="c-sidebar__logoText">
                                            <NobulaLogoTextIcon />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="c-sidebar__closemenuButton" onClick={() => dispatch(toggleCollapseAction())}>
                            {collapsed ? <ArrowRightIcon /> : <ArrowLeftIcon />}
                        </div>
                    </SidebarHeader>
                    <div className="c-sidebar__contentAndFooter">
                        <SidebarContent>
                            {filteredMenuItemsForRole
                                .filter((item) => !item.footer)
                                .map((menuItem, index) => (
                                    <SidebarItem key={index} item={menuItem} collapsed={collapsed} />
                                ))}
                        </SidebarContent>
                        <SidebarFooter>
                            {filteredMenuItemsForRole
                                .filter((item) => item.footer)
                                .map((menuItem, index) => (
                                    <SidebarItem key={index} item={menuItem} collapsed={collapsed} />
                                ))}
                        </SidebarFooter>
                    </div>
                </ProSidebar>
            </div>
        </>
    );
}

export default Sidebar;
